
import { IAnonymousUserLoginService, IAnonymousResponseUser, IPageController, IAnonymousPageObjectTreeService } from "..";
import { BusinessException, CatchError } from "@core/error";
import { BasePageController } from "../base-page-controller";
import { IResponseEvent } from "@core/data-access/model/response-event";
import { IError, IResponse } from "@core/data-access/model";
import { PromptTypeEnum } from "@modules/base/decorator/error/PromptType.enum";
import { envConfig } from "@env/env-config";

export abstract class AnonymousPageController extends BasePageController implements IPageController {


    private readonly OBJECT_TREE: IAnonymousPageObjectTreeService;
    private readonly LOGIN_SERVICE: IAnonymousUserLoginService;

    public rootPath: string;
    public pageContext: qpmn.main.IPageContext;
    public $scope: angular.IScope;

    constructor($objectTree: IAnonymousPageObjectTreeService, loginService: IAnonymousUserLoginService) {
        super();

        this.OBJECT_TREE = $objectTree;
        this.LOGIN_SERVICE = loginService;
    }

    private readonly PROMPT_DEFAULT_CONFIG = {
        // 是否反向显示confirm和取消按钮
        reverseButtons: true,
        // 是否显示取消按钮
        showCancelButton: false,
        // 取消按钮的自定义class类
        cancelButtonClass: 'alert-login-cancel',
        // 确认按钮的自定义class类
        confirmButtonClass: 'alert-login-confirm',
        // 为按钮添加默认的swal2样式
        buttonsStyling: true,
        // 是否允许点击对话框外部来关闭对话框
        allowOutsideClick: false,
        // 是否允许按下Esc键来关闭对话框
        allowEscapeKey: false,
        title: "Permission invalid",
        content: "Go to login?",
        cancelButtonText: "CANCEL",
        confirmButtonText: "OK"
    }

    /**
     * @deprecated
     * @description 
     * Rest请求统一错误处理
     * 实名用户:提示当前用户已过期,是否前往登录(是:跳转至登录页,否:重新获取匿名用户token)
     * 匿名用户:重新获取匿名用户token
     * @param e 
     */
    // TODO请求失败无权限测试
    // @CatchError()
    public onRequestFail<T extends IError>(e: IResponseEvent<T>) {
        
        // e类型判断
        if (e != null && typeof e === "object") {
            //没有访问权限
            if (e.status == 401) {
                // 匿名用户id, 如果发起请求的anonymousUserId为 0 则表示新建匿名用户
                let anonymousUserId: number = this.OBJECT_TREE.getAnonymousUserId();
    
                // 检查当前是否是实名用户
                let isAnonymous = this.OBJECT_TREE.isAnonymous();
    
                // 匿名用户处理
                if (isAnonymous) {
                    this.resetAnonymousToken();
                } else {
                    let config = angular.copy(this.PROMPT_DEFAULT_CONFIG);
                    config.showCancelButton = true;
    
                    // 实名用户处理
                    window.qpAlertPrompt[PromptTypeEnum.QUESTION]("Missing Authentication", "You don't have authentication or it is expired, click OK to login again or click Cancel to visit this page as an anonymous user.", {
                        fn: () => {
                            this.OBJECT_TREE.setAnonymous(true);
                            // 记录当前页面地址并跳转至登录页
                            this.goToLogin();
                        }
                    }, { fn: () => { this.resetAnonymousToken(); } }, config);
                }
            } else if (e.status == 404) {
                // 请求404报错处理
                window.qpAlertPrompt[PromptTypeEnum.ERROR]("Connection Failed", `Oops! Connection failed when you were trying to pass data to the server. Please check your network connection. Alternatively, you can refresh this page and try again. If the problem still exists, please contact our technical support ${this.supportmail}`);
            } else if (e.status == 504) {
                window.qpAlertPrompt[PromptTypeEnum.WARNING]("Network connection timeout", "There was a network fluctuation,which caused the request to timeout. Please try again later.");
            } else if (e.status == -1) {
                window.qpAlertPrompt[PromptTypeEnum.INFO]("No internet connection", "Your network is unavailable, Check your data or Wi-Fi connection.");
            } else {
                // 通用错误
                this.commonErrorHandle(e);
            }
        } else {
            this.commonErrorHandle(e);
        }
    }


    // @CatchError()
    public onRequestFailed<T extends IError>(e: IResponseEvent<T>): angular.IPromise<T> {
    
        let $translate = this.getInjectable("$translate");
        let $msgDialog = this.getInjectable("qpMessageDialog");
        let defer = this.getInjectable("$q").defer<T>();
        
        if (e != null && typeof e === "object") {
            //没有访问权限
            if (e.status == 401) {
                // 匿名用户id, 如果发起请求的anonymousUserId为 0 则表示新建匿名用户
                let anonymousUserId: number = this.OBJECT_TREE.getAnonymousUserId();
    
                // 检查当前是否是实名用户
                let isAnonymous = this.OBJECT_TREE.isAnonymous();
    
                // 匿名用户处理
                // 此处builde定制页
                if (isAnonymous) {
                    this.resetAnonymousToken();
                } else {
                    // 此处builde定制页需要使用
                    // 实名用户处理
                    $msgDialog.info({
                        title: $translate.instant("MISSING_AUTHENTICATION_TITLE"),
                        content: $translate.instant("MISSING_AUTHENTICATION_CONTENT"),
                        buttons: [
                            "ok",
                            {
                                key: "cancel",
                                displayName: "cancel",
                                style: "text"
                            }
                        ]
                    }).then(res => {
                        if (res == "ok") {
                            this.OBJECT_TREE.setAnonymous(true);
                            // 记录当前页面地址并跳转至登录页
                            this.goToLogin();
                        } else if (res == "cancel") {
                            this.resetAnonymousToken();
                        }
                    })
                }
            } else if (e.status == 404) {
                // 请求404报错处理
                $msgDialog.error({
                    title: $translate.instant("SYSTEM_ERROR_TITLE"),
                    content: $translate.instant("SYSTEM_ERROR_CONTENT", { supportmail: envConfig.supportEmail }),
                    code: e.status,
                    msg: e.config.url
                })
            } else if (e.status == 504) {
                $msgDialog.info({
                    title: $translate.instant("NETWORK_CONNECTION_TIMEOUT_TITLE"),
                    content: $translate.instant("NETWORK_CONNECTION_TIMEOUT_CONTENT"),
                    code: e.status,
                    msg: e.config.url
                })
            } else if (e.status == 200) {
                // 不同Partner不可访问
                if (e.data?.data?.code == "104000001") {
                    $msgDialog.info({
                        title: $translate.instant("LACKING_PERMISSION_TITLE"),
                        content: $translate.instant("LACKING_PERMISSION_CONTENT"),
                        buttons: [
                            "ok"
                        ]
                    }).then(res => {
                        if (res == "ok") {
                            // 跳转至登录页
                            this.goToLogin(false);
                        }
                    })
                } else {
                    defer.resolve(e.data.data);
                }
            } else if (e.status == -1) {
                $msgDialog.info({
                    title: $translate.instant("NETWORK_UNAVAILABLE_TITLE"),
                    content: $translate.instant("NETWORK_UNAVAILABLE_CONTENT")
                })
            } else {
                this.commonErrorHandle(e);
            }
        } else {
            this.commonErrorHandle(e);
        }

        return defer.promise;
    }

    /**
     * @deprecated
     */
    public goToLogin(isRecordCurrentPageHref: boolean = true) {
        let root: string;
        if (this.rootPath != undefined && this.rootPath != "") {
            root = this.rootPath;
        } else if (this.pageContext != undefined && this.pageContext.rootPath != undefined && this.pageContext.rootPath != "") {
            root = this.pageContext.rootPath
        } else {
            root = envConfig.appPath();
        }

        if (root.endsWith("/")) {
            root = root.slice(0, -1);
        }

        // 是否记录当前页面href
        if (isRecordCurrentPageHref) {
            window.location.href = root + "/partner/login/" + "?" + encodeURIComponent(location.href);
        } else {
            window.location.href = root + "/partner/login/";
        }
    }




    /**
     * 
     * @deprecated
    * @description 检查用户页面访问权限及Token过期处理(可匿名访问)
    */
    // @CatchError()
    public checkAuthorization(successFunc: Function) {
        let currentToken = this.OBJECT_TREE.token?.access_token;
        // 有token且没有过期则直接初始化上下文
        if (currentToken && !this.OBJECT_TREE.isExpire()) {
            this.authBroadcast();
            successFunc();
        } else if (currentToken && this.OBJECT_TREE.isExpire()) {
            // 有token且已过期,重置为匿名用户token
            this.resetAnonymousToken(() => {
                this.authBroadcast();
                successFunc();
            })
        } else {
            // 无token,获取匿名token，初始化上下文
            this.resetAnonymousToken(() => {
                this.authBroadcast();
                successFunc();
            })
        }
    }


    /**
     * @description 重置当前匿名
     * @deprecated
     * @param successCallback 
     * @param failCallback 
     */
    public resetAnonymousToken(successCallback?, failCallback?) {
        // 获取匿名用户id, 如果发起请求的anonymousUserId为 0 则表示新建匿名用户
        let anonymousUserId: number = this.OBJECT_TREE.getAnonymousUserId() || 0;

        this.LOGIN_SERVICE.login(anonymousUserId).then((tokenResp: IResponse<IAnonymousResponseUser>) => {
            // Token信息存至缓存
            // this.OBJECT_TREE.setLogin({
            //     firstName: tokenResp.data.firstName,
            //     userName: tokenResp.data.username,
            //     token: tokenResp.data.access_token,
            //     tokenType: tokenResp.data.token_type,
            //     isAnonymous: true,
            //     anonymousUserId: tokenResp.data.anonymousUserId,
            //     expire: tokenResp.data.expires_in,
            //     partnerType: tokenResp.data.partnerType,
            // })
            this.OBJECT_TREE.setAnonymousLogin({
                token: tokenResp.data.access_token,
                tokenType: tokenResp.data.token_type,
                isAnonymous: true,
                anonymousUserId: tokenResp.data.abc,
                expire: tokenResp.data.expires_in,
            })
            if (successCallback) {
                successCallback();
            }
            // todo: 是否需要reload
            window.location.reload();  // builder页实名登录过期刷新
        }, (failResp) => {
            // 登录失败处理
            //TODO userId不存在处理,暂时未知什么原因会导致这个问题发生,在这做兼容处理,userId不存在时,重新执行匿名登录,后续通过Sentry采集异常
            if (failResp.status === 200 && failResp.data && !angular.isUndefined(failResp.data.success) && !failResp.data.success && !angular.isUndefined(failResp.data.data.code) && failResp.data.data.code === 3000001) {

                // 清除缓存中记录的匿名用户id
                this.OBJECT_TREE.removeCache("AnonymousUserId");
                // 重新登录
                this.resetAnonymousToken(successCallback, failCallback);

                // this.LOGIN_SERVICE.login(0).then((tokenResp: IResponse<IAnonymousResponseUser>) => {
                //     // Token信息存至缓存
                //     this.OBJECT_TREE.setLogin({
                //         firstName: tokenResp.data.firstName,
                //         userName: tokenResp.data.username,
                //         token: tokenResp.data.access_token,
                //         tokenType: tokenResp.data.token_type,
                //         isAnonymous: true,
                //         anonymousUserId: tokenResp.data.anonymousUserId,
                //         expire: tokenResp.data.expires_in,
                //         partnerType: tokenResp.data.partnerType,
                //     })
                // }, (reLoginResp) => {
                //     this.onRequestFail(reLoginResp);
                // })
            } else {
                this.onRequestFail(failResp);
            }
        })
    }

}